import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LocationProvider } from "@/context/LocationContext";
import { Spinner } from "@/components/States";

import Home from "@/pages/Home";
import Stores from "@/pages/Stores";
import StoreDetail from "@/pages/StoreDetail";
import ProductDetail from "@/pages/ProductDetail";
import SearchPage from "@/pages/Search";
import Checkout from "@/pages/Checkout";
import Orders from "@/pages/Orders";
import OrderTracking from "@/pages/OrderTracking";
import Account from "@/pages/Account";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RetailerOnboarding from "@/pages/RetailerOnboarding";
import CategoryPage from "@/pages/CategoryPage";
import AllCategories from "@/pages/AllCategories";
import Support from "@/pages/Support";
import RetailerDashboard from "@/pages/retailer/RetailerDashboard";
import DeliveryDashboard from "@/pages/delivery/DeliveryDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SupportAgentDashboard from "@/pages/agent/SupportAgentDashboard";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import StartStore from "@/pages/StartStore";
import SurveyDashboard from "@/pages/survey/SurveyDashboard";
import ShyamaHome from "@/shyama/ShyamaHome";
import Community from "@/pages/Community";
import ShyamaApply from "@/shyama/ShyamaApply";
import ShyamaFarms from "@/shyama/ShyamaFarms";
import ShyamaFarm from "@/shyama/ShyamaFarm";
import ShyamaAccount from "@/shyama/ShyamaAccount";
import { ShyamaStatus, ShyamaDonate, ShyamaHowItWorks, ShyamaStories } from "@/shyama/ShyamaPages";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/stores" element={<Stores />} />
              <Route path="/community" element={<Community />} />
              <Route path="/stores/:id" element={<StoreDetail />} />
              <Route path="/store/:id" element={<StoreDetail />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/categories" element={<AllCategories />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/partner" element={<RetailerOnboarding />} />
              <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
              <Route path="/orders" element={<Protected><Orders /></Protected>} />
              <Route path="/order/:id" element={<Protected><OrderTracking /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/support" element={<Protected><Support /></Protected>} />
              <Route path="/retailer" element={<Protected role="retailer"><RetailerDashboard /></Protected>} />
              <Route path="/delivery" element={<Protected role="delivery"><DeliveryDashboard /></Protected>} />
              <Route path="/admin" element={<Protected role={["admin", "sub_admin"]}><AdminDashboard /></Protected>} />
              <Route path="/agent" element={<Protected role="support_agent"><SupportAgentDashboard /></Protected>} />
              <Route path="/affiliate" element={<Protected role="influencer"><AffiliateDashboard /></Protected>} />
              <Route path="/survey" element={<Protected role="survey_agent"><SurveyDashboard /></Protected>} />
              <Route path="/shyama-foundation" element={<ShyamaHome />} />
              <Route path="/shyama-foundation/apply" element={<ShyamaApply />} />
              <Route path="/shyama-foundation/farms" element={<ShyamaFarms />} />
              <Route path="/shyama-foundation/products" element={<ShyamaFarms />} />
              <Route path="/shyama-foundation/farm/:slug" element={<ShyamaFarm />} />
              <Route path="/shyama-foundation/donate" element={<ShyamaDonate />} />
              <Route path="/shyama-foundation/how-it-works" element={<ShyamaHowItWorks />} />
              <Route path="/shyama-foundation/stories" element={<ShyamaStories />} />
              <Route path="/shyama-foundation/status/:id" element={<Protected><ShyamaStatus /></Protected>} />
              <Route path="/shyama-foundation/account" element={<Protected><ShyamaAccount /></Protected>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/start-store" element={<StartStore />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </CartProvider>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
